import { Lock } from 'lucide-react';
import { Switch as Primitive } from 'radix-ui';
import { type ReactNode, useId } from 'react';

import { cn } from './cn.ts';

export interface SwitchRowProps {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Shown instead of the switch being interactive: "your instructor set this". */
  lockedReason?: string | undefined;
}

/**
 * The control by itself, for a row that already says what it is.
 *
 * It needs a name from somewhere — `aria-label`, or `aria-labelledby` pointing at the
 * text beside it — because a switch with no name is a switch nobody can use by ear.
 */
export function Switch({
  checked,
  onCheckedChange,
  disabled,
  className,
  ...named
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean | undefined;
  className?: string | undefined;
  'aria-label'?: string | undefined;
  'aria-labelledby'?: string | undefined;
}) {
  return (
    <Primitive.Root
      {...named}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      className={cn(
        'relative h-5 w-9 shrink-0 rounded-full bg-border-strong transition',
        'data-[state=checked]:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
    >
      <Primitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition data-[state=checked]:translate-x-[18px]" />
    </Primitive.Root>
  );
}

/** A labelled on/off switch with an explanation, as every settings toggle is shown. */
export function SwitchRow({
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  lockedReason,
}: SwitchRowProps) {
  const id = useId();
  const locked = lockedReason !== undefined;
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <div className="min-w-0">
        <label htmlFor={id} className="flex items-center gap-1.5 text-sm font-medium text-fg">
          {label}
          {locked ? <Lock size={12} className="text-fg-subtle" aria-hidden /> : null}
        </label>
        {description ? <p className="mt-0.5 text-xs text-fg-muted">{description}</p> : null}
        {locked ? <p className="mt-0.5 text-xs text-warning">{lockedReason}</p> : null}
      </div>
      <Primitive.Root
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        disabled={disabled === true || locked}
        className={cn(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full bg-border-strong transition',
          'data-[state=checked]:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <Primitive.Thumb className="block size-4 translate-x-0.5 rounded-full bg-white shadow transition data-[state=checked]:translate-x-[18px]" />
      </Primitive.Root>
    </div>
  );
}
